import { useCallback, useMemo, useState } from 'react';
import { getDefaultGeoDays } from '../utils/geoConstants';
import {
  GEO_FILTER_DEFAULTS,
  GEO_COMPETITORS_DEFAULT,
  hasGeoInsightsFiltersApplied,
  hasGeoPromptsFiltersApplied,
  buildGeoInsightsParams,
  buildGeoPromptsParams,
} from '../utils/geoFilters';

/**
 * Shared filter state for GEO Insights + Prompts pages.
 */
export default function useGeoFilters({
  mode = 'insights',
  config = null,
  promptsPage = 1,
  promptsPageSize = 25,
} = {}) {
  const defaultDays = getDefaultGeoDays(mode);
  const [days, setDays] = useState(defaultDays);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [filterCompetitors, setFilterCompetitors] = useState(GEO_COMPETITORS_DEFAULT);
  const [sentimentType, setSentimentType] = useState(GEO_FILTER_DEFAULTS.sentimentType);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTableFilters, setShowTableFilters] = useState(false);

  const competitorCatalog = useMemo(
    () => (config?.competitors || []).map(String),
    [config?.competitors],
  );

  const insightsParams = useMemo(
    () => buildGeoInsightsParams({
      days,
      useCustomRange,
      dateFrom,
      dateTo,
      filterCompetitors,
      competitorCatalog,
    }),
    [
      days,
      useCustomRange,
      dateFrom,
      dateTo,
      filterCompetitors,
      competitorCatalog,
    ],
  );

  const promptsParams = useMemo(
    () => buildGeoPromptsParams({
      page: promptsPage,
      pageSize: promptsPageSize,
      searchTerm,
      sentimentType,
      days,
      useCustomRange,
      dateFrom,
      dateTo,
    }),
    [
      promptsPage,
      promptsPageSize,
      searchTerm,
      sentimentType,
      days,
      useCustomRange,
      dateFrom,
      dateTo,
    ],
  );

  const hasFiltersApplied = mode === 'prompts'
    ? hasGeoPromptsFiltersApplied({
      days,
      useCustomRange,
      dateFrom,
      dateTo,
      searchTerm,
      sentimentType,
    })
    : hasGeoInsightsFiltersApplied({
      days,
      useCustomRange,
      dateFrom,
      dateTo,
      filterCompetitors,
      competitorCatalog,
    });

  const handleClearFilters = useCallback(() => {
    setDays(defaultDays);
    setUseCustomRange(false);
    setDateFrom(null);
    setDateTo(null);
    setTempDateRange([null, null]);
    setFilterCompetitors(GEO_COMPETITORS_DEFAULT);
    setSentimentType(GEO_FILTER_DEFAULTS.sentimentType);
    setSearchTerm('');
  }, [defaultDays]);

  const handleDaysChange = useCallback((value) => {
    if (value === 'custom') {
      setUseCustomRange(true);
      setTempDateRange([dateFrom, dateTo]);
      return;
    }
    setUseCustomRange(false);
    setDays(Number(value));
    setDateFrom(null);
    setDateTo(null);
    setTempDateRange([null, null]);
  }, [dateFrom, dateTo]);

  const applyCustomDateRange = useCallback(() => {
    const [start, end] = tempDateRange;
    setDateFrom(start || null);
    setDateTo(end || null);
    setUseCustomRange(true);
  }, [tempDateRange]);

  const applyFilters = useCallback((next) => {
    setDays(next.days);
    setUseCustomRange(next.useCustomRange);
    setDateFrom(next.dateFrom);
    setDateTo(next.dateTo);
    setTempDateRange(next.useCustomRange ? [next.dateFrom, next.dateTo] : [null, null]);
    setFilterCompetitors(next.filterCompetitors);
    setSentimentType(next.sentimentType);
  }, []);

  return {
    mode,
    defaultDays,
    days,
    setDays,
    useCustomRange,
    dateFrom,
    dateTo,
    tempDateRange,
    setTempDateRange,
    filterCompetitors,
    setFilterCompetitors,
    sentimentType,
    setSentimentType,
    searchTerm,
    setSearchTerm,
    showTableFilters,
    setShowTableFilters,
    hasFiltersApplied,
    handleClearFilters,
    handleDaysChange,
    applyCustomDateRange,
    applyFilters,
    insightsParams,
    promptsParams,
    competitorCatalog,
    clientBrand: config?.clientBrand || '',
  };
}
